import {
    Check,
    ChevronDown,
    ChevronRight,
    Dumbbell,
    Plus,
    Star,
    Trash2,
} from "lucide-react";

import { useState } from "react";
import clsx from "clsx";
import { SportCover } from "@/components/sport/SportMedia";
import { Alert } from "@/components/ui/Alert";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardContent, CardHeader } from "@/components/ui/Card";
import { Input } from "@/components/ui/Field";
import { Modal } from "@/components/ui/Modal";
import { EmptyState, ErrorState, LoadingBlock } from "@/components/ui/States";
import { ApiError } from "@/lib/api";
import { useI18n } from "@/hooks/useI18n";
import {
    useAddCoachSport,
    useCoachSports,
    useRemoveCoachSport,
    useSetPrimaryCoachSport,
    useSports,
} from "./useCoachSports";

/**
 * Menu Sport — coach mengelola olahraganya sendiri (`coach_sports`):
 * tambah dari master, tandai utama, hapus. Master `sports` tidak disentuh
 * karena itu ranah merchant/core Flexv.
 */
export function SportsPage() {
    const { t } = useI18n();
    const [selected, setSelected] = useState("");
    const [error, setError] = useState<string | null>(null);
    const [pickerOpen, setPickerOpen] = useState(false);
    const [query, setQuery] = useState("");

    const { data, isPending, isError, refetch } = useCoachSports();
    const { data: masterSports, isPending: masterPending } = useSports();
    const addSport = useAddCoachSport();
    const setPrimary = useSetPrimaryCoachSport();
    const removeSport = useRemoveCoachSport();

    const ownedSportIds = new Set((data?.items ?? []).map((item) => item.sportId));

    // Sumber pilihan: master `/sports` (ikon & cover sudah URL absolut).
    // `available` dari `/coach/sports` jadi fallback bila master belum termuat.
    const pickerOptions = (masterSports ?? data?.available ?? []).map((sport) => ({
        ...sport,
        owned: ownedSportIds.has(sport.id),
    }));

    // Pencarian dipakai di dalam modal picker (nama + deskripsi).
    const filteredOptions = pickerOptions.filter((sport) =>
        `${sport.name} ${sport.description ?? ""}`
            .toLowerCase()
            .includes(query.trim().toLowerCase()),
    );

    const selectedSport = pickerOptions.find((sport) => sport.id === selected) ?? null;
    const canAdd = Boolean(selectedSport) && !selectedSport?.owned;

    const handleError = (caught: unknown) => {
        setError(
            caught instanceof ApiError ? caught.message : t("sports.actionFailed"),
        );
    };

    const handleAdd = async () => {
        if (!selected) return;
        setError(null);
        try {
            await addSport.mutateAsync({ sportId: selected });
            setSelected("");
        } catch (caught) {
            handleError(caught);
        }
    };

    const handleTogglePrimary = async (id: string, next: boolean) => {
        setError(null);
        try {
            await setPrimary.mutateAsync({ id, isPrimary: next });
        } catch (caught) {
            handleError(caught);
        }
    };

    const handleRemove = async (id: string) => {
        setError(null);
        try {
            await removeSport.mutateAsync(id);
        } catch (caught) {
            handleError(caught);
        }
    };

    return (
        <div className="flex flex-col gap-6">
            <header>
                <h1 className="app-text-primary text-2xl font-bold sm:text-3xl">
                    {t("sports.title")}
                </h1>
                <p className="app-shell-text-muted mt-1 text-sm">{t("sports.subtitle")}</p>
            </header>

            <Card>
                <CardHeader
                    title={t("sports.add.title")}
                    description={t("sports.add.subtitle")}
                    icon={<Plus className="h-5 w-5" />}
                />
                <CardContent className="flex flex-col gap-4">
                    <div className="flex flex-col gap-2">
                        <span className="app-text-soft text-xs font-semibold uppercase tracking-wide">
                            {t("sports.add.sport")}
                        </span>

                        {/* Pemicu picker: pratinjau pilihan (ikon + nama +
                            deskripsi), daftar lengkapnya ada di modal supaya
                            semua olahraga pasti bisa dijangkau. */}
                        <button
                            type="button"
                            onClick={() => setPickerOpen(true)}
                            aria-haspopup="dialog"
                            className="app-shell-border group flex items-center gap-3 rounded-2xl border p-3 text-left transition-all duration-200 hover:shadow-sm"
                        >
                            {selectedSport ? (
                                <SportCover sport={selectedSport} size="lg" />
                            ) : (
                                <span className="app-content-subtle grid h-16 w-16 shrink-0 place-items-center rounded-2xl">
                                    <Dumbbell className="app-text-soft h-6 w-6" />
                                </span>
                            )}

                            <span className="min-w-0 flex-1">
                                <span className="app-text-primary block truncate text-lg font-bold tracking-tight">
                                    {selectedSport
                                        ? selectedSport.name
                                        : masterPending
                                          ? t("sports.loading")
                                          : t("sports.add.placeholder")}
                                </span>
                                {selectedSport?.description ? (
                                    <span className="app-text-soft mt-0.5 block truncate text-sm">
                                        {selectedSport.description}
                                    </span>
                                ) : (
                                    <span className="app-text-soft mt-0.5 block truncate text-xs">
                                        {t("sports.picker.open")}
                                    </span>
                                )}
                            </span>

                            <ChevronDown className="app-shell-text-muted h-4 w-4 shrink-0 transition-transform duration-200 group-hover:translate-y-0.5" />
                        </button>

                        {!masterPending && pickerOptions.length === 0 && data && (
                            <p className="app-text-soft text-xs">{t("sports.add.emptyMaster")}</p>
                        )}
                    </div>

                    {/* Satu baris aksi: ringkasan pilihan + tombol tambah.
                        Baris teks ganda kemarin dihapus supaya kartu tidak
                        terlihat berlapis-lapis. */}
                    <div className="app-content-subtle flex items-center gap-3 rounded-2xl px-3.5 py-3">
                        <p className="min-w-0 flex-1 truncate text-sm">
                            {selectedSport ? (
                                <>
                                    <span className="app-text-primary font-semibold">
                                        {selectedSport.name}
                                    </span>
                                    <span className="app-text-soft">
                                        {" "}
                                        · {t("sports.add.ready")}
                                    </span>
                                </>
                            ) : (
                                <span className="app-text-soft">{t("sports.add.hint")}</span>
                            )}
                        </p>

                        <Button
                            size="sm"
                            icon={<Plus className="h-4 w-4" />}
                            loading={addSport.isPending}
                            disabled={!canAdd}
                            onClick={() => void handleAdd()}
                        >
                            {t("sports.add.submit")}
                        </Button>
                    </div>

                    {error && <Alert tone="error">{error}</Alert>}
                </CardContent>
            </Card>

            {/* Picker olahraga — semua master ditampilkan di sini. */}
            <Modal
                open={pickerOpen}
                onClose={() => setPickerOpen(false)}
                title={t("sports.picker.title")}
                description={t("sports.picker.subtitle")}
            >
                <div className="flex flex-col gap-4">
                    <Input
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                        placeholder={t("sports.picker.search")}
                        aria-label={t("sports.picker.search")}
                    />

                    {filteredOptions.length === 0 && (
                        <p className="app-text-soft text-sm">{t("sports.picker.noResult")}</p>
                    )}

                    {/* Daftar berbaris: ikon monokrom + nama + deskripsi,
                        tanpa warna aksen di tiap baris supaya tenang. */}
                    <ul className="app-shell-border divide-y rounded-2xl border">
                        {filteredOptions.map((sport) => {
                            const isSelected = selected === sport.id;

                            return (
                                <li key={sport.id}>
                                    <button
                                        type="button"
                                        disabled={sport.owned}
                                        aria-pressed={isSelected}
                                        title={sport.owned ? t("sports.add.owned") : sport.name}
                                        onClick={() => {
                                            setSelected(sport.id);
                                            setPickerOpen(false);
                                            setQuery("");
                                        }}
                                        className={clsx(
                                            "flex w-full items-center gap-3 px-3.5 py-3 text-left transition-colors duration-150 first:rounded-t-2xl last:rounded-b-2xl",
                                            sport.owned
                                                ? "app-shell-text-muted cursor-not-allowed opacity-60"
                                                : "app-text-primary hover:app-content-subtle-hover",
                                            isSelected && !sport.owned && "app-content-subtle",
                                        )}
                                    >
                                        {/* Cover (image_url → icon_url → generik)
                                            supaya Golf yang tanpa ikon tetap
                                            tampil dengan gambar aslinya. */}
                                        <span
                                            className={
                                                sport.owned ? "opacity-60 grayscale" : undefined
                                            }
                                        >
                                            <SportCover sport={sport} size="md" />
                                        </span>

                                        <span className="min-w-0 flex-1">
                                            <span className="block truncate text-base font-bold tracking-tight">
                                                {sport.name}
                                            </span>
                                            {sport.description && (
                                                <span className="app-text-soft mt-0.5 block truncate text-sm">
                                                    {sport.description}
                                                </span>
                                            )}
                                        </span>

                                        {sport.owned ? (
                                            <Badge tone="neutral">{t("sports.add.owned")}</Badge>
                                        ) : isSelected ? (
                                            <Check className="app-text-soft h-4 w-4 shrink-0" />
                                        ) : (
                                            <ChevronRight className="app-shell-text-muted h-4 w-4 shrink-0" />
                                        )}
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            </Modal>

            <Card>
                <CardHeader
                    title={t("sports.list.title")}
                    icon={<Dumbbell className="h-5 w-5" />}
                />
                <CardContent noPadding>
                    {isPending && <LoadingBlock label={t("sports.loading")} />}

                    {isError && (
                        <ErrorState
                            message={t("sports.loadFailed")}
                            onRetry={() => void refetch()}
                        />
                    )}

                    {data && data.items.length === 0 && (
                        <EmptyState
                            icon={<Dumbbell className="h-6 w-6" />}
                            title={t("sports.empty.title")}
                            description={t("sports.empty.description")}
                        />
                    )}

                    {data && data.items.length > 0 && (
                        <ul className="flex flex-col gap-2 p-4">
                            {data.items.map((coachSport) => (
                                <li
                                    key={coachSport.id}
                                    className="app-shell-border group flex items-center gap-3 rounded-2xl border p-3 transition-all duration-200 hover:shadow-sm"
                                >
                                    {/* Daftar pakai cover gambar (`image_url`) —
                                        ukuran lg supaya olahraga terasa
                                        "hero", bukan thumbnail kecil. */}
                                    <SportCover sport={coachSport.sport} size="lg" />

                                    <div className="min-w-0 flex-1">
                                        <p className="app-text-primary flex items-center gap-2 truncate text-lg font-bold tracking-tight">
                                            <span className="min-w-0 truncate">
                                                {coachSport.sport?.name}
                                            </span>
                                            {coachSport.isPrimary && (
                                                <Badge tone="primary">
                                                    {t("sports.primary")}
                                                </Badge>
                                            )}
                                        </p>
                                        <p className="app-text-soft mt-0.5 truncate text-sm">
                                            {coachSport.sport?.description ??
                                                t("sports.list.added")}
                                        </p>
                                    </div>

                                    {/* Aksi compact: bintang toggle-utama (icon-only)
                                        + tombol hapus icon-only. */}
                                    <div className="flex shrink-0 items-center gap-1.5">
                                        <button
                                            type="button"
                                            title={
                                                coachSport.isPrimary
                                                    ? t("sports.action.unsetPrimary")
                                                    : t("sports.action.setPrimary")
                                            }
                                            aria-pressed={coachSport.isPrimary}
                                            aria-label={
                                                coachSport.isPrimary
                                                    ? t("sports.action.unsetPrimary")
                                                    : t("sports.action.setPrimary")
                                            }
                                            disabled={setPrimary.isPending}
                                            onClick={() =>
                                                void handleTogglePrimary(
                                                    coachSport.id,
                                                    !coachSport.isPrimary,
                                                )
                                            }
                                            className={clsx(
                                                "rounded-xl p-2.5 transition-all duration-200 disabled:opacity-50",
                                                coachSport.isPrimary
                                                    ? "bg-amber-400/15 text-amber-500"
                                                    : "app-content-subtle app-shell-text-muted hover:app-text-primary hover:app-content-subtle-hover",
                                            )}
                                        >
                                            <Star
                                                className={clsx(
                                                    "h-4 w-4",
                                                    coachSport.isPrimary && "fill-current",
                                                )}
                                            />
                                        </button>

                                        <button
                                            type="button"
                                            title={t("sports.action.remove")}
                                            aria-label={t("sports.action.remove")}
                                            disabled={removeSport.isPending}
                                            onClick={() => void handleRemove(coachSport.id)}
                                            className="app-content-subtle app-shell-text-muted rounded-xl p-2.5 transition-all duration-200 hover:bg-[var(--color-error-bg)] hover:text-[var(--color-error-text)] disabled:opacity-50"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </button>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
